import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { CopyIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { EtpaOrder } from "@/client-admin/types.gen.ts";
import { DataTableColumnHeader } from "../UserTable/data-table-column-header";

export const columns: ColumnDef<EtpaOrder>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
        className="translate-y-[2px]"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="ID" />,
    cell: ({ row }) => {
      const id = row.getValue("id") as string | null;
      if (!id) return <div>-</div>;

      const truncatedId = id.length > 8 ? `${id.slice(0, 8)}...` : id;

      return (
        <div className="flex items-center gap-2">
          <code className="text-sm">{truncatedId}</code>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => {
              navigator.clipboard.writeText(id);
              toast({
                title: "Copied",
                description: "ID copied to clipboard",
              });
            }}
            title="Copy full ID"
          >
            <CopyIcon className="h-4 w-4" />
          </Button>
        </div>
      );
    },
  },
  {
    accessorKey: "orderType",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Order Type" />,
    enableColumnFilter: true,
    cell: ({ row }) => {
      const value = row.getValue("orderType") as string | null | undefined;
      if (!value) return <div>-</div>;

      const className = getOrderTypeColor(value);
      return (
        <Badge className={className} variant="outline">
          {value}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: "individualId",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Individual" />,
    cell: ({ row }) => <div>{row.getValue("individualId") ?? "-"}</div>,
  },
  {
    accessorKey: "xbidOrderType",
    header: ({ column }) => <DataTableColumnHeader column={column} title="XBID Type" />,
    enableColumnFilter: true,
    cell: ({ row }) => {
      const value = row.getValue("xbidOrderType") as string | null | undefined;
      if (!value) return <div>-</div>;

      return <Badge variant="outline">{value}</Badge>;
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: "capacity",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Capacity" />,
    cell: ({ row }) => {
      const value = row.getValue("capacity") as number | null | undefined;
      if (value === null || value === undefined) return <div>-</div>;
      return <div>{value} MW</div>;
    },
  },
  {
    accessorKey: "originalCapacity",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Original Capacity" />,
    cell: ({ row }) => {
      const value = row.getValue("originalCapacity") as number | null | undefined;
      if (value === null || value === undefined) return <div>-</div>;
      return <div>{value} MW</div>;
    },
  },
  {
    accessorKey: "unitPrice",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Unit Price" />,
    cell: ({ row }) => {
      const value = row.getValue("unitPrice") as number | null | undefined;
      if (value === null || value === undefined) return <div>-</div>;
      return <div>€{value}/MWh</div>;
    },
  },
  {
    accessorKey: "totalPrice",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Total Price" />,
    cell: ({ row }) => {
      const value = row.getValue("totalPrice") as number | null | undefined;
      if (value === null || value === undefined) return <div>-</div>;
      return <div>€{value}</div>;
    },
  },
  {
    accessorKey: "deliveryStartTime",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Delivery Start" />,
    cell: ({ row }) => {
      const timestamp = row.getValue("deliveryStartTime") as number;
      return <div>{format(new Date(timestamp), "yyyy-MM-dd HH:mm") ?? "-"}</div>;
    },
  },
  {
    accessorKey: "deliveryEndTime",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Delivery End" />,
    cell: ({ row }) => {
      const timestamp = row.getValue("deliveryEndTime") as number;
      return <div>{format(new Date(timestamp), "yyyy-MM-dd HH:mm") ?? "-"}</div>;
    },
  },
  {
    accessorKey: "orderStatus",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    enableColumnFilter: true,
    cell: ({ row }) => {
      const value = row.getValue("orderStatus") as string | null | undefined;
      if (!value) return <div>-</div>;

      const className = getOrderStatusColor(value);
      return (
        <Badge className={className} variant="outline">
          {value}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    id: "intradayCode",
    accessorFn: (row) => (row.metadata?.intradayCode as string | undefined) ?? "",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Intraday Code" />,
    cell: ({ row }) => {
      const intradayCode = row.getValue("intradayCode") as string;
      if (!intradayCode) return <div>-</div>;
      return <code className="text-sm">{intradayCode}</code>;
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: "created",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
    cell: ({ row }) => {
      const timestamp = row.getValue("created") as number;
      return <div>{format(new Date(timestamp), "yyyy-MM-dd HH:mm:ss") ?? "-"}</div>;
    },
  },
];

export const orderTypes = [
  {
    label: "BUY",
    value: "BUY",
  },
  {
    label: "SELL",
    value: "SELL",
  },
];

export const orderStatuses = [
  {
    label: "ACTIVE",
    value: "ACTIVE",
  },
  {
    label: "INACTIVE",
    value: "INACTIVE",
  },
  {
    label: "EXECUTED",
    value: "EXECUTED",
  },
  {
    label: "CANCELLED",
    value: "CANCELLED",
  },
];

export const xbidOrderTypes = [
  {
    label: "REGULAR",
    value: "REGULAR",
  },
  {
    label: "ICEBERG",
    value: "ICEBERG",
  },
];

export function getUniqueIntradayCodesFromEtpaOrders(data: EtpaOrder[]): { label: string; value: string }[] {
  const uniqueCodes = [
    ...new Set(data.map((order) => (order.metadata?.intradayCode as string | undefined) ?? "").filter(Boolean)),
  ];
  return uniqueCodes.sort().map((code) => ({ label: code, value: code }));
}

function getOrderTypeColor(orderType: string | null | undefined): string {
  if (!orderType) return "bg-gray-100 text-gray-800";
  return orderType === "BUY" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
}

function getOrderStatusColor(orderStatus: string | null | undefined): string {
  if (!orderStatus) return "bg-gray-100 text-gray-800";

  switch (orderStatus) {
    case "ACTIVE":
      return "bg-blue-100 text-blue-800";
    case "EXECUTED":
      return "bg-green-100 text-green-800";
    case "CANCELLED":
      return "bg-red-100 text-red-800";
    case "INACTIVE":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
}
